/* ── Reports & Analytics — Types & Mock Data ── */

export interface ReportGroup {
    title: string;
    reports: ReportMeta[];
}

export interface ReportMeta {
    id: string;
    name: string;
    description: string;
}

/* ── Report menu ── */
export const REPORT_MENU: ReportGroup[] = [
    {
        title: 'Financial Reports',
        reports: [
            { id: 'dailyCollection', name: 'Daily Collection', description: 'Daily fee collection summary with totals' },
        ],
    },
    {
        title: 'Academic Reports',
        reports: [
            { id: 'reportCards', name: 'Report Cards', description: 'Student-wise marksheets for printing' },
        ],
    },
    {
        title: 'Attendance Reports',
        reports: [
            { id: 'monthlyAttendance', name: 'Monthly Register', description: 'Day-wise attendance matrix for a class' },
        ],
    },
    {
        title: 'Transport Reports',
        reports: [
            { id: 'routeManifest', name: 'Route Manifest', description: 'Student list grouped by transport stops' },
        ],
    },
];

/* ═══ Data shapes ═══ */

export interface DailyCollectionRow {
    receipt_no: string;
    student_name: string;
    class_name: string;
    amount: number;
    payment_method: string;
    time: string;
}

export interface AttendanceCell {
    day: number;
    status: 'P' | 'A' | 'L' | '-';
}

export interface AttendanceRow {
    student_name: string;
    roll_number: number;
    days: AttendanceCell[];
    total_present: number;
    total_absent: number;
    total_leave: number;
}

export interface SubjectMark {
    subject: string;
    max_marks: number;
    obtained: number;
    grade: string;
}

export interface ReportCardData {
    student_name: string;
    enrollment_number: string;
    class_name: string;
    section_name: string;
    roll_number: number;
    marks: SubjectMark[];
    total_obtained: number;
    total_max: number;
    percentage: number;
    result: 'Pass' | 'Fail';
    rank: number;
}

export interface RouteStop {
    stop_name: string;
    pickup_time: string;
    students: { name: string; class_name: string; parent_phone: string }[];
}

export interface RouteManifestData {
    route_name: string;
    vehicle_number: string;
    driver_name: string;
    stops: RouteStop[];
}

/* ═══ Mock generators ═══ */
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function generateDailyCollection(): Promise<DailyCollectionRow[]> {
    await delay(500);
    return [
        { receipt_no: 'RCP-001', student_name: 'Aarav Sharma', class_name: 'Grade 5-A', amount: 12500, payment_method: 'Cash', time: '09:15 AM' },
        { receipt_no: 'RCP-002', student_name: 'Diya Patel', class_name: 'Grade 3-B', amount: 8200, payment_method: 'Online', time: '09:42 AM' },
        { receipt_no: 'RCP-003', student_name: 'Rohan Gupta', class_name: 'Grade 7-A', amount: 15000, payment_method: 'Cheque', time: '10:05 AM' },
        { receipt_no: 'RCP-004', student_name: 'Ananya Singh', class_name: 'Grade 2-A', amount: 6500, payment_method: 'Cash', time: '10:30 AM' },
        { receipt_no: 'RCP-005', student_name: 'Vihaan Reddy', class_name: 'Grade 8-B', amount: 18000, payment_method: 'Bank Transfer', time: '11:00 AM' },
        { receipt_no: 'RCP-006', student_name: 'Ishita Joshi', class_name: 'Grade 1-A', amount: 5800, payment_method: 'Online', time: '11:20 AM' },
        { receipt_no: 'RCP-007', student_name: 'Kabir Mehta', class_name: 'Grade 6-C', amount: 14200, payment_method: 'Cash', time: '11:45 AM' },
        { receipt_no: 'RCP-008', student_name: 'Prisha Iyer', class_name: 'Grade 4-B', amount: 9500, payment_method: 'Online', time: '12:10 PM' },
        { receipt_no: 'RCP-009', student_name: 'Arjun Nair', class_name: 'Grade 10-A', amount: 22000, payment_method: 'Bank Transfer', time: '01:30 PM' },
        { receipt_no: 'RCP-010', student_name: 'Saanvi Kumar', class_name: 'Grade 9-A', amount: 19800, payment_method: 'Cheque', time: '02:15 PM' },
        { receipt_no: 'RCP-011', student_name: 'Reyansh Das', class_name: 'Grade 5-B', amount: 12500, payment_method: 'Cash', time: '02:50 PM' },
        { receipt_no: 'RCP-012', student_name: 'Myra Verma', class_name: 'Grade 3-A', amount: 7800, payment_method: 'Online', time: '03:20 PM' },
    ];
}

export async function generateAttendance(): Promise<AttendanceRow[]> {
    await delay(500);
    const names = ['Aarav Sharma', 'Ananya Singh', 'Arjun Nair', 'Diya Patel', 'Ishita Joshi', 'Kabir Mehta', 'Myra Verma', 'Prisha Iyer', 'Reyansh Das', 'Rohan Gupta', 'Saanvi Kumar', 'Vihaan Reddy'];
    const statuses: ('P' | 'A' | 'L')[] = ['P', 'A', 'L'];

    return names.map((name, i) => {
        const days: AttendanceCell[] = [];
        let tp = 0, ta = 0, tl = 0;
        for (let d = 1; d <= 28; d++) {
            const r = Math.random();
            const s = r < 0.82 ? 'P' : r < 0.92 ? 'A' : 'L';
            days.push({ day: d, status: s as typeof statuses[number] });
            if (s === 'P') tp++; else if (s === 'A') ta++; else tl++;
        }
        for (let d = 29; d <= 31; d++) days.push({ day: d, status: '-' });
        return { student_name: name, roll_number: i + 1, days, total_present: tp, total_absent: ta, total_leave: tl };
    });
}

export async function generateReportCards(): Promise<ReportCardData[]> {
    await delay(500);
    const students = [
        { name: 'Aarav Sharma', enr: 'ENR-2025-001', roll: 1 },
        { name: 'Diya Patel', enr: 'ENR-2025-002', roll: 2 },
        { name: 'Rohan Gupta', enr: 'ENR-2025-003', roll: 3 },
        { name: 'Ananya Singh', enr: 'ENR-2025-004', roll: 4 },
    ];

    const subjectPool = ['Mathematics', 'Science', 'English', 'Hindi', 'Social Studies', 'Computer Science'];
    const gradeFor = (pct: number): string => pct >= 90 ? 'A+' : pct >= 75 ? 'A' : pct >= 60 ? 'B' : pct >= 45 ? 'C' : 'D';

    return students.map((s, idx) => {
        const marks: SubjectMark[] = subjectPool.map((sub) => {
            const maxM = 100;
            const obt = Math.floor(55 + Math.random() * 45);
            return { subject: sub, max_marks: maxM, obtained: obt, grade: gradeFor((obt / maxM) * 100) };
        });
        const totalO = marks.reduce((a, m) => a + m.obtained, 0);
        const totalM = marks.reduce((a, m) => a + m.max_marks, 0);
        const pct = Math.round((totalO / totalM) * 100 * 10) / 10;
        return {
            student_name: s.name, enrollment_number: s.enr, class_name: 'Grade 5', section_name: 'A',
            roll_number: s.roll, marks, total_obtained: totalO, total_max: totalM, percentage: pct,
            result: pct >= 40 ? 'Pass' as const : 'Fail' as const, rank: idx + 1,
        };
    });
}

export async function generateRouteManifest(): Promise<RouteManifestData> {
    await delay(500);
    return {
        route_name: 'Route 3 — Koramangala to School',
        vehicle_number: 'KA-01-AB-1234',
        driver_name: 'Ramesh Yadav',
        stops: [
            {
                stop_name: 'Koramangala 4th Block',
                pickup_time: '07:15 AM',
                students: [
                    { name: 'Aarav Sharma', class_name: 'Grade 5-A', parent_phone: '+91 98765 43210' },
                    { name: 'Diya Patel', class_name: 'Grade 3-B', parent_phone: '+91 87654 32109' },
                ],
            },
            {
                stop_name: 'Koramangala 8th Block',
                pickup_time: '07:25 AM',
                students: [
                    { name: 'Rohan Gupta', class_name: 'Grade 7-A', parent_phone: '+91 76543 21098' },
                    { name: 'Ananya Singh', class_name: 'Grade 2-A', parent_phone: '+91 65432 10987' },
                    { name: 'Vihaan Reddy', class_name: 'Grade 8-B', parent_phone: '+91 54321 09876' },
                ],
            },
            {
                stop_name: 'Madiwala Junction',
                pickup_time: '07:35 AM',
                students: [
                    { name: 'Ishita Joshi', class_name: 'Grade 1-A', parent_phone: '+91 43210 98765' },
                    { name: 'Kabir Mehta', class_name: 'Grade 6-C', parent_phone: '+91 32109 87654' },
                ],
            },
            {
                stop_name: 'Silk Board Signal',
                pickup_time: '07:45 AM',
                students: [
                    { name: 'Prisha Iyer', class_name: 'Grade 4-B', parent_phone: '+91 21098 76543' },
                    { name: 'Arjun Nair', class_name: 'Grade 10-A', parent_phone: '+91 10987 65432' },
                    { name: 'Saanvi Kumar', class_name: 'Grade 9-A', parent_phone: '+91 09876 54321' },
                    { name: 'Reyansh Das', class_name: 'Grade 5-B', parent_phone: '+91 98012 34567' },
                ],
            },
        ],
    };
}
